import apiClient from "../http/apiClient";
import { emitLogout } from "../http/authEvents";
import { AUTH_ENDPOINTS } from "../http/endpoints";
import { clearTokens, getRefreshToken, setTokens } from "../http/tokenStore";

export async function login(payload) {
  const response = await apiClient.post(AUTH_ENDPOINTS.login, payload, {
    skipAuth: true,
    skipRefresh: true,
  });

  const access = response?.data?.access;
  const refresh = response?.data?.refresh;

  if (access && refresh) {
    setTokens({ access, refresh });
  }

  return response.data;
}

export async function refreshSession() {
  const refresh = getRefreshToken();
  if (!refresh) {
    throw new Error("Refresh token not available.");
  }

  const response = await apiClient.post(
    AUTH_ENDPOINTS.refresh,
    { refresh },
    {
      skipAuth: true,
      skipRefresh: true,
    }
  );

  const access = response?.data?.access;
  const rotatedRefresh = response?.data?.refresh || refresh;

  if (!access) {
    throw new Error("Refresh response did not include an access token.");
  }

  setTokens({
    access,
    refresh: rotatedRefresh,
  });

  return {
    access,
    refresh: rotatedRefresh,
  };
}

export async function logout() {
  const refresh = getRefreshToken();

  try {
    if (refresh) {
      await apiClient.post(
        AUTH_ENDPOINTS.logout,
        { refresh },
        {
          skipRefresh: true,
        }
      );
    }
  } catch {
    // Logout is best-effort: local session must end even if token is expired/invalid.
  } finally {
    clearTokens();
    emitLogout({ reason: "manual_logout" });
  }
}

export async function getMe() {
  const response = await apiClient.get(AUTH_ENDPOINTS.me);
  return response.data;
}

export async function changePassword(payload) {
  const response = await apiClient.post(AUTH_ENDPOINTS.changePassword, payload);
  return response.data;
}

export async function verifyPasswordResetIdentity(payload) {
  const response = await apiClient.post(AUTH_ENDPOINTS.passwordResetVerify, payload, {
    skipAuth: true,
    skipRefresh: true,
  });
  return response.data;
}

export async function confirmPasswordReset(payload) {
  const response = await apiClient.post(AUTH_ENDPOINTS.passwordResetConfirm, payload, {
    skipAuth: true,
    skipRefresh: true,
  });
  return response.data;
}

export async function createInvite(payload) {
  const response = await apiClient.post(AUTH_ENDPOINTS.invites, payload);
  return response.data;
}

export async function validateInvite(token) {
  const response = await apiClient.get(AUTH_ENDPOINTS.invitesValidate, {
    params: { token },
    skipAuth: true,
    skipRefresh: true,
  });
  return response.data;
}

export async function acceptInvite(payload) {
  const normalizedPayload =
    payload && !payload.password2 && payload.password
      ? { ...payload, password2: payload.password }
      : payload;

  clearTokens();
  const response = await apiClient.post(AUTH_ENDPOINTS.invitesAccept, normalizedPayload, {
    skipAuth: true,
    skipRefresh: true,
  });
  const access = response?.data?.access;
  const refresh = response?.data?.refresh;

  if (access && refresh) {
    setTokens({ access, refresh });
  }

  return response.data;
}

export const authService = {
  login,
  refreshSession,
  logout,
  changePassword,
  verifyPasswordResetIdentity,
  confirmPasswordReset,
  getMe,
  createInvite,
  validateInvite,
  acceptInvite,
};

export default authService;
